/**
 * Zone3Comptabilite — Paiements Stripe encaissés + Commission cabinet + Net proprio.
 * Batch #14.156 refactor — extracted from AdminImmeubleZones.jsx.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calculator, FileBarChart, Loader2, Download, Lock,
  Receipt, Banknote, Trash2, Settings, RotateCcw,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "@/lib/toast";
import {
  API, authH, dollars, PAYMENT_METHOD_LABEL,
  ZoneHeader, Field, KPI,
} from "./_shared";

export default function Zone3Comptabilite({ masterId, overview }) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [payments, setPayments] = useState([]);
  const [yearPayments, setYearPayments] = useState([]);
  const [journal, setJournal] = useState({ revenues: [], total_revenues: 0 });
  const [loading, setLoading] = useState(false);
  const [showCommission, setShowCommission] = useState(false);
  const [commission, setCommission] = useState({ pct: 0, flat: 0 });
  const [stripeDefaults, setStripeDefaults] = useState({ commission_pct: 3.0, commission_flat: 0, fee_pct: 2.9, fee_flat: 0.30 });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/stripe-config`, { headers: authH() });
        const rent = r.data?.config?.rent_collection || {};
        setStripeDefaults({
          commission_pct: Number(rent.cabinet_commission_percentage ?? 3.0),
          commission_flat: Number(rent.cabinet_commission_fixed ?? 0),
          fee_pct: Number(rent.stripe_fee_percentage ?? 2.9),
          fee_flat: Number(rent.stripe_fee_fixed ?? 0.30),
        });
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const c = overview?.master?.immeuble_commission || {};
    const pct = Number(c.pct || 0);
    const flat = Number(c.flat || 0);
    if (pct === 0 && flat === 0) {
      setCommission({ pct: stripeDefaults.commission_pct, flat: stripeDefaults.commission_flat });
    } else {
      setCommission({ pct, flat });
    }
  }, [overview, stripeDefaults]);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const [p, py, j] = await Promise.all([
        axios.get(`${API}/admin/immeuble/${masterId}/zone3/payments?year=${year}&month=${month}`, { headers: authH() }),
        axios.get(`${API}/admin/immeuble/${masterId}/zone3/payments?year=${year}`, { headers: authH() }),
        axios.get(`${API}/admin/immeuble/${masterId}/zone3/journal?year=${year}`, { headers: authH() }),
      ]);
      setPayments(p.data?.items || []);
      setYearPayments(py.data?.items || []);
      setJournal({
        revenues: j.data?.revenues || [],
        total_revenues: j.data?.total_revenues || 0,
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [masterId, year, month]);

  useEffect(() => { load(); }, [load]);

  const manualGross = journal.revenues.reduce((s, r) => s + Number(r.amount || 0), 0);
  const stripeGross = payments.reduce((s, p) => s + Number(p.amount_cad || 0), 0);
  const totalGross = stripeGross + manualGross;
  const totalCount = payments.length + journal.revenues.length;
  const commissionAmount = (totalGross * commission.pct / 100) + (commission.flat * totalCount);
  const stripeFees = (totalGross * stripeDefaults.fee_pct / 100) + (stripeDefaults.fee_flat * totalCount);
  const netToOwner = totalGross - commissionAmount - stripeFees;

  const monthlyData = useMemo(() => {
    const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jui","Jul","Aoû","Sep","Oct","Nov","Déc"];
    const buckets = MONTHS.map((m) => ({ m, brut: 0, frais: 0, commission: 0, count: 0 }));
    for (const p of yearPayments) {
      const mo = Number(p.period_month || 0);
      if (mo < 1 || mo > 12) continue;
      const amt = Number(p.amount_cad || 0);
      buckets[mo - 1].brut += amt;
      buckets[mo - 1].count += 1;
    }
    for (const r of journal.revenues) {
      const dStr = (r.paid_at || "").slice(0, 10);
      const moRaw = Number(r.period_month || (dStr ? dStr.split("-")[1] : 0));
      if (moRaw < 1 || moRaw > 12) continue;
      const amt = Number(r.amount || 0);
      buckets[moRaw - 1].brut += amt;
      buckets[moRaw - 1].count += 1;
    }
    for (const b of buckets) {
      b.frais = +((b.brut * stripeDefaults.fee_pct / 100) + (stripeDefaults.fee_flat * b.count)).toFixed(2);
      b.commission = +((b.brut * commission.pct / 100) + (commission.flat * b.count)).toFixed(2);
      b.brut = +b.brut.toFixed(2);
    }
    return buckets;
  }, [yearPayments, journal.revenues, stripeDefaults, commission]);

  const generateStatement = async () => {
    try {
      const r = await axios.get(
        `${API}/admin/immeuble/${masterId}/zone3/monthly-statement?year=${year}&month=${month}`,
        { headers: authH(), responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      toast.success("Relevé mensuel généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const saveCommission = async () => {
    try {
      await axios.put(`${API}/admin/immeuble/${masterId}/zone3/commission`, commission, { headers: authH() });
      toast.success("Commission enregistrée");
      setShowCommission(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const resetToStripeDefaults = () => {
    setCommission({ pct: stripeDefaults.commission_pct, flat: stripeDefaults.commission_flat });
    toast.message("Valeurs des paramètres Stripe restaurées (non sauvegardées)");
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="admin-immeuble-zone-3">
      <ZoneHeader index={3} title="Données financières — Stripe" icon={Calculator} tone="blue"
        subtitle="Paiements Stripe encaissés · Frais (déductibles) · Commission cabinet · Net propriétaire" />

      <div className="p-5 space-y-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                    className="cc-input py-1 text-xs" data-testid="zone3-year">
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                    className="cc-input py-1 text-xs" data-testid="zone3-month">
              {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"]
                .map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <button
            onClick={() => setShowCommission(!showCommission)}
            className="text-[11px] px-2.5 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
            data-testid="zone3-commission-toggle"
          >
            <Settings className="w-3 h-3" /> Commission : {commission.pct}% + {commission.flat}$/paiement
          </button>
        </div>

        {showCommission && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="zone3-commission-form">
            <p className="text-[10px] text-blue-700">
              Valeurs par défaut tirées des paramètres Stripe globaux :
              <strong> {stripeDefaults.commission_pct}% + {dollars(stripeDefaults.commission_flat)}/paiement</strong>.
              Modifiez ici uniquement si ce dossier diffère.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Commission %">
                <input type="number" step="0.1" value={commission.pct}
                       onChange={(e) => setCommission({ ...commission, pct: Number(e.target.value) })}
                       className="cc-input font-mono" data-testid="zone3-commission-pct" />
              </Field>
              <Field label="Commission fixe ($/paiement)">
                <input type="number" step="0.01" value={commission.flat}
                       onChange={(e) => setCommission({ ...commission, flat: Number(e.target.value) })}
                       className="cc-input font-mono" data-testid="zone3-commission-flat" />
              </Field>
              <div className="flex items-end gap-1">
                <button onClick={saveCommission} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700"
                        data-testid="zone3-commission-save">
                  Enregistrer
                </button>
                <button onClick={resetToStripeDefaults} title="Restaurer les valeurs des paramètres Stripe globaux"
                        className="px-2 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-xs inline-flex items-center gap-1"
                        data-testid="zone3-commission-reset">
                  <RotateCcw className="w-3 h-3" /> Défauts
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Encaissé brut" value={dollars(totalGross)} tone="emerald" testId="zone3-kpi-gross" />
          <KPI label={`Frais Stripe (${stripeDefaults.fee_pct}% + ${stripeDefaults.fee_flat}$)`}
               value={`(${dollars(stripeFees)})`} tone="rose" testId="zone3-kpi-stripe-fees" />
          <KPI label="Commission cabinet" value={`(${dollars(commissionAmount)})`} tone="amber" testId="zone3-kpi-commission" />
          <KPI label="Net à verser proprio" value={dollars(netToOwner)} tone="blue" testId="zone3-kpi-net" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider inline-flex items-center gap-2">
              <Banknote className="w-3.5 h-3.5 text-emerald-600" /> Paiements Stripe encaissés
            </h3>
            <button onClick={generateStatement} disabled={payments.length === 0}
                    className="text-[11px] px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="zone3-generate-statement">
              <Download className="w-3 h-3" /> Relevé mensuel PDF
            </button>
          </div>
          {loading ? (
            <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400 inline" /></div>
          ) : payments.length === 0 ? (
            <p className="text-xs text-slate-400 italic px-2 py-3">Aucun paiement Stripe encaissé sur cette période.</p>
          ) : (
            <table className="w-full text-xs" data-testid="zone3-payments-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="py-1">Date</th><th>Locataire</th><th>Logement</th><th className="text-right">Brut</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 text-slate-600">{(p.paid_at || "").slice(0, 10) || `${p.period_year}-${String(p.period_month).padStart(2, "0")}`}</td>
                    <td className="text-slate-700">{p.tenant_name || "—"}</td>
                    <td className="text-slate-500">{p.unit_label || "—"}</td>
                    <td className="text-right font-mono">{dollars(p.amount_cad)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 inline-flex items-center gap-2">
            <Receipt className="w-3.5 h-3.5 text-emerald-600" />
            Revenus manuels (Conciliation) — {dollars(journal.total_revenues)}
            <Lock className="w-3 h-3 text-slate-400" />
          </h3>
          <p className="text-[10px] text-slate-500 mb-2">
            Entrées saisies via <strong>Comptabilité → Conciliation manuelle</strong> (Interac, comptant, chèque).
            Les paiements Stripe ne sont PAS dupliqués ici.
          </p>
          {journal.revenues.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucun revenu manuel saisi pour {year}.</p>
          ) : (
            <ul className="space-y-1 text-xs max-h-48 overflow-y-auto" data-testid="zone3-manual-revenues">
              {journal.revenues.slice(0, 20).map((r) => (
                <li key={r.id} className="flex items-center gap-2 bg-emerald-50 px-2 py-1 rounded group">
                  <span className="text-[10px] text-slate-500">{(r.paid_at || "").slice(0, 10)}</span>
                  <span className="flex-1 truncate text-slate-700">{r.tenant_name || "—"}</span>
                  <span className="text-[10px] text-slate-500 px-1.5 py-0 rounded bg-white border">{PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}</span>
                  <span className="font-mono text-slate-800">{dollars(r.amount)}</span>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Supprimer ce revenu de ${dollars(r.amount)} ?\n\nCette action est définitive et ne peut être annulée.`)) return;
                      try {
                        await axios.delete(`${API}/admin/pms/manual-revenues/${r.id}`, { headers: authH() });
                        toast.success("Revenu supprimé");
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erreur suppression");
                      }
                    }}
                    className="opacity-0 group-hover:opacity-100 transition px-1.5 py-0.5 rounded text-rose-600 hover:bg-rose-100"
                    data-testid={`zone3-revenue-delete-${r.id}`}
                    title="Supprimer ce revenu"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider inline-flex items-center gap-2">
              <FileBarChart className="w-3.5 h-3.5 text-blue-600" />
              Vue annuelle — Encaissé / Frais Stripe / Commission ({year})
            </h3>
          </div>
          <div className="h-52" data-testid="zone3-monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="m" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip formatter={(v) => dollars(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="brut" fill="#10b981" radius={[3, 3, 0, 0]} name="Encaissé brut" />
                <Bar dataKey="frais" fill="#f43f5e" radius={[3, 3, 0, 0]} name="Frais Stripe" />
                <Bar dataKey="commission" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Commission cabinet" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
